import { HugeiconsIcon } from "@hugeicons/react";
import { AnalyticsDownIcon, AnalyticsUpIcon, BarChartIcon, MinusSignIcon } from "@hugeicons/core-free-icons";
import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { api } from '@/lib/api';
import { PageHeader, EmptyState } from '@/components/common/Bits';
import { fmtDate } from '@/lib/constants';
import { Card, CardContent } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { MotionSection, StaggerContainer, StaggerItem } from '@/components/shared/MotionSection';
import { AxisCard } from '@/components/shared/AxisCard';
import { DemoDataBadge } from '@/components/shared/DemoDataBadge';
import { cn } from '@/lib/utils';

export default function ValueMonitoring() {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    api.get('/metrics').then(({ data }) => setRows(data));
  }, []);

  if (!rows)
    return (
      <div className="space-y-6">
        <Skeleton className="h-20 rounded-2xl" />
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[...Array(6)].map((_, i) => (
            <Skeleton key={i} className="h-44 rounded-2xl" />
          ))}
        </div>
      </div>
    );

  const grouped = rows.reduce((acc, m) => {
    (acc[m.initiative_name] = acc[m.initiative_name] || []).push(m);
    return acc;
  }, {});

  return (
    <div className="max-w-7xl mx-auto px-6 py-10 space-y-8">
      <MotionSection>
        <div
          className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4"
          aria-labelledby="value-title">
          <div>
            <h1
              id="value-title"
              className="font-display text-2xl md:text-3xl font-extrabold text-foreground leading-tight">
              Monitoreo de valor
            </h1>
            <p className="text-sm text-muted-foreground mt-1.5 max-w-lg">
              Resultados reales del portafolio comparados con el valor esperado y las metas comprometidas.
            </p>
          </div>
          <DemoDataBadge />
        </div>
      </MotionSection>

      <MotionSection delay={0.05}>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <AxisCard axis="personas" value="—" confidence="estimado" />
          <AxisCard axis="operacion" value="—" confidence="estimado" />
          <AxisCard axis="productividad" value="—" confidence="estimado" />
          <AxisCard axis="confianza" value="—" confidence="estimado" />
        </div>
      </MotionSection>

      {rows.length === 0 && (
        <EmptyState
          icon={AnalyticsUpIcon}
          title="Sin métricas registradas"
          subtitle="Registre resultados reales desde la ficha de cada iniciativa."
        />
      )}
      <div className="space-y-8">
        {Object.entries(grouped).map(([name, metrics], groupIdx) => (
          <MotionSection key={name} delay={0.1 + groupIdx * 0.05}>
            <div>
              <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                <h2 className="font-display text-lg font-bold">{name}</h2>
                <Badge variant="outline" className="bg-primary/8 text-primary border-primary/20 text-xs">
                  Valor esperado: {metrics[0].expected_value}
                </Badge>
              </div>
              <StaggerContainer className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" stagger={0.04}>
                {metrics.map((m) => {
                  const trend =
                    m.previous == null ? null : m.value > m.previous ? 'up' : m.value < m.previous ? 'down' : 'flat';
                  const pct = m.target ? Math.min(100, Math.round((m.value / m.target) * 100)) : null;
                  return (
                    <StaggerItem key={m.id}>
                      <Card
                        className="shadow-card border-border rounded-2xl hover:shadow-float transition-shadow duration-300"
                        data-testid={`metric-card-${m.id}`}>
                        <CardContent className="p-5">
                          <div className="flex items-center justify-between">
                            <p className="text-xs font-medium text-muted-foreground">
                              {m.metric} · {m.period}
                            </p>
                            {trend === 'up' && (
                              <HugeiconsIcon icon={AnalyticsUpIcon} className="h-4 w-4 text-success" aria-label="Tendencia positiva" />
                            )}
                            {trend === 'down' && (
                              <HugeiconsIcon icon={AnalyticsDownIcon} className="h-4 w-4 text-risk-high" aria-label="Tendencia negativa" />
                            )}
                            {trend === 'flat' && (
                              <HugeiconsIcon icon={MinusSignIcon} className="h-4 w-4 text-muted-foreground" aria-label="Sin cambio" />
                            )}
                          </div>
                          <p className="font-display text-4xl font-bold mt-3 tabular">
                            {m.value}
                            <span className="text-base font-normal text-muted-foreground ml-1">{m.unit}</span>
                          </p>
                          {pct != null && (
                            <div className="mt-4">
                              <div className="flex justify-between text-[11px] text-muted-foreground mb-1.5">
                                <span>
                                  Meta: {m.target} {m.unit}
                                </span>
                                <span
                                  className={cn(
                                    'font-bold',
                                    pct >= 80 ? 'text-success' : pct >= 50 ? 'text-warning' : 'text-risk-high',
                                  )}>
                                  {pct}%
                                </span>
                              </div>
                              <Progress value={pct} className="h-2" />
                            </div>
                          )}
                          <div className="mt-4 pt-3 border-t border-border text-[11px] text-muted-foreground space-y-0.5">
                            <p>
                              Anterior: {m.previous ?? '—'} {m.unit}
                            </p>
                            <p>
                              Fuente: {m.source} · Owner: {m.owner}
                            </p>
                            <p>Actualizado: {fmtDate(m.updated_at)}</p>
                          </div>
                        </CardContent>
                      </Card>
                    </StaggerItem>
                  );
                })}
              </StaggerContainer>
            </div>
          </MotionSection>
        ))}
      </div>
      <p className="text-xs text-muted-foreground mt-4">
        Para registrar nuevas métricas, abra la ficha de la iniciativa en el{' '}
        <Link to="/registro" className="text-primary underline underline-offset-2">
          AI Registry
        </Link>
        .
      </p>
    </div>
  );
}
